import { ArrowLeft, Compass, LogIn, Sprout, User } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { MarkdownPreview } from "../components/MarkdownPreview";
import { loadPublicAuthors, loadPublicFeed, loadPublicObject } from "../lib/api";
import type { PublicAuthor, PublicItem } from "../types";

function formatPublicDate(value?: string) {
  const timestamp = value ? Date.parse(value) : NaN;

  if (Number.isNaN(timestamp)) {
    return "";
  }

  return new Intl.DateTimeFormat("vi-VN", { dateStyle: "medium" }).format(new Date(timestamp));
}

// Public, UNAUTHENTICATED sharing page. /explore = feed (+ author filter); /p/:id = single node.
export function Explore() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [feed, setFeed] = useState<PublicItem[]>([]);
  const [authors, setAuthors] = useState<PublicAuthor[]>([]);
  const [author, setAuthor] = useState<string | undefined>(undefined);
  const [single, setSingle] = useState<PublicItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Single-node view
  useEffect(() => {
    if (!id) {
      return;
    }
    setLoading(true);
    setError("");
    loadPublicObject(id)
      .then((res) => setSingle(res.data))
      .catch(() => setError("Không tìm thấy nội dung này (có thể đã chuyển về riêng tư)."))
      .finally(() => setLoading(false));
  }, [id]);

  // Feed view
  useEffect(() => {
    if (id) {
      return;
    }
    setLoading(true);
    Promise.all([loadPublicFeed(author), loadPublicAuthors()])
      .then(([f, a]) => {
        setFeed(f.data);
        setAuthors(a.data);
      })
      .catch(() => setError("Không tải được feed."))
      .finally(() => setLoading(false));
  }, [id, author]);

  return (
    <main className="min-h-screen bg-paper text-ink">
      <header className="sticky top-0 z-20 border-b border-line bg-white/95 backdrop-blur">
        <div className="mx-auto flex min-h-16 max-w-[900px] items-center gap-3 px-4 py-3 sm:px-6">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-blue-100 bg-blue-50 text-brand">
            <Sprout size={21} aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-base font-bold leading-tight">Human Note</p>
            <p className="inline-flex items-center gap-1 text-xs font-medium text-muted">
              <Compass size={12} aria-hidden="true" /> Khám phá · nội dung công khai
            </p>
          </div>
          <Link
            to="/"
            className="inline-flex h-10 items-center gap-2 rounded-lg border border-line bg-white px-3 text-sm font-semibold text-slate-600 hover:bg-slate-50"
          >
            <LogIn size={16} aria-hidden="true" /> Đăng nhập / Đăng ký
          </Link>
        </div>
      </header>

      <div className="mx-auto max-w-[900px] px-4 py-6 sm:px-6">
        {loading ? <p className="text-sm text-muted">Đang tải...</p> : null}
        {error ? <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700">{error}</p> : null}

        {/* Single-node view */}
        {id && single ? (
          <article className="rounded-lg border border-line bg-white p-5 shadow-panel">
            <button
              type="button"
              onClick={() => navigate("/explore")}
              className="mb-3 inline-flex items-center gap-1.5 text-sm font-semibold text-muted hover:text-brand"
            >
              <ArrowLeft size={15} aria-hidden="true" /> Về feed
            </button>
            <h1 className="text-2xl font-bold">{single.title}</h1>
            <p className="mt-1 inline-flex items-center gap-1.5 text-sm text-muted">
              <User size={13} aria-hidden="true" /> {single.author} · {single.kind}
              {formatPublicDate(single.createdAt) ? <> · {formatPublicDate(single.createdAt)}</> : null}
            </p>
            <div className="mt-4 border-t border-line pt-4">
              <MarkdownPreview content={single.content ?? ""} />
            </div>
          </article>
        ) : null}

        {/* Feed view */}
        {!id ? (
          <>
            <div className="mb-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setAuthor(undefined)}
                className={`rounded-full border px-3 py-1 text-sm font-semibold ${!author ? "border-blue-200 bg-blue-50 text-brand" : "border-line bg-white text-slate-600 hover:bg-slate-50"}`}
              >
                Tất cả
              </button>
              {authors.map((a) => (
                <button
                  key={a.username}
                  type="button"
                  onClick={() => setAuthor(a.username)}
                  className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-sm font-semibold ${author === a.username ? "border-blue-200 bg-blue-50 text-brand" : "border-line bg-white text-slate-600 hover:bg-slate-50"}`}
                >
                  <User size={12} aria-hidden="true" /> {a.username} ({a.publicCount})
                </button>
              ))}
            </div>

            {!loading && feed.length === 0 ? (
              <div className="rounded-lg border border-dashed border-line px-4 py-12 text-center text-sm text-muted">
                Chưa có nội dung công khai nào.
              </div>
            ) : null}

            <ul className="space-y-3">
              {feed.map((item) => (
                <li key={item.id}>
                  <Link
                    to={`/p/${item.id}`}
                    className="block rounded-lg border border-line bg-white p-4 shadow-sm hover:border-blue-200 hover:bg-blue-50/30"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <h2 className="font-bold text-ink">{item.title}</h2>
                      <span className="shrink-0 rounded bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">{item.kind}</span>
                    </div>
                    <p className="mt-1 inline-flex items-center gap-1 text-xs text-muted">
                      <User size={11} aria-hidden="true" /> {item.author}
                      {formatPublicDate(item.createdAt) ? <> · {formatPublicDate(item.createdAt)}</> : null}
                    </p>
                    {item.content ? (
                      <p className="mt-2 line-clamp-2 text-sm text-slate-600">{item.content.replace(/[#*`>_]/g, "").slice(0, 200)}</p>
                    ) : null}
                  </Link>
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </div>
    </main>
  );
}
